import { useCallback, useEffect, useRef, useState } from 'react';

interface VaultSource {
  vault?: boolean;
  id?: string | number;
}

interface UseVaultStateOptions {
  aiSolutionData: VaultSource | null;
  selectedSolutionFromLibrary: VaultSource | null;
  updatedAiSolution: VaultSource | null;
  selectedSolutionId?: string | number | null;
  handleToggleVault: () => void | Promise<void | boolean>;
}

interface UseVaultStateResult {
  localVaultState: boolean;
  isVaultActionLoading: boolean;
  handleVaultClick: () => Promise<void>;
}

const resolveVaultState = (
  selectedSolutionFromLibrary: VaultSource | null,
  aiSolutionData: VaultSource | null,
  updatedAiSolution: VaultSource | null
): boolean =>
  aiSolutionData?.vault ??
  selectedSolutionFromLibrary?.vault ??
  updatedAiSolution?.vault ??
  false;

export const useVaultState = ({
  aiSolutionData,
  selectedSolutionFromLibrary,
  updatedAiSolution,
  selectedSolutionId,
  handleToggleVault,
}: UseVaultStateOptions): UseVaultStateResult => {
  const [isVaultActionLoading, setIsVaultActionLoading] = useState(false);
  const [localVaultState, setLocalVaultState] = useState<boolean>(() =>
    resolveVaultState(selectedSolutionFromLibrary, aiSolutionData, updatedAiSolution)
  );

  const vaultStateManuallyChangedRef = useRef(false);
  const trackedSolutionIdRef = useRef<string | number | null>(selectedSolutionId ?? null);

  useEffect(() => {
    const activeSolutionId =
      selectedSolutionId ??
      selectedSolutionFromLibrary?.id ??
      aiSolutionData?.id ??
      null;

    if (activeSolutionId !== trackedSolutionIdRef.current) {
      trackedSolutionIdRef.current = activeSolutionId;
      vaultStateManuallyChangedRef.current = false;
      setLocalVaultState(
        resolveVaultState(selectedSolutionFromLibrary, aiSolutionData, updatedAiSolution)
      );
      return;
    }

    if (!vaultStateManuallyChangedRef.current) {
      setLocalVaultState(
        resolveVaultState(selectedSolutionFromLibrary, aiSolutionData, updatedAiSolution)
      );
    }
  }, [
    selectedSolutionId,
    selectedSolutionFromLibrary?.id,
    selectedSolutionFromLibrary?.vault,
    aiSolutionData?.id,
    aiSolutionData?.vault,
    updatedAiSolution?.vault,
  ]);

  const handleVaultClick = useCallback(async () => {
    if (isVaultActionLoading) return;
    setIsVaultActionLoading(true);
    vaultStateManuallyChangedRef.current = true;

    setLocalVaultState((prev) => !prev);

    try {
      const result = handleToggleVault?.();
      const resolved = await Promise.resolve(result);

      if (typeof resolved === 'boolean') {
        setLocalVaultState(resolved);
        setTimeout(() => {
          vaultStateManuallyChangedRef.current = false;
        }, 400);
      } else {
        setTimeout(() => {
          vaultStateManuallyChangedRef.current = false;
        }, 500);
      }
    } catch (err) {
      console.error('Vault action failed', err);
      setLocalVaultState(
        resolveVaultState(selectedSolutionFromLibrary, aiSolutionData, updatedAiSolution)
      );
      vaultStateManuallyChangedRef.current = false;
    } finally {
      setTimeout(() => setIsVaultActionLoading(false), 300);
    }
  }, [
    handleToggleVault,
    isVaultActionLoading,
    selectedSolutionFromLibrary,
    aiSolutionData,
    updatedAiSolution,
  ]);

  return { localVaultState, isVaultActionLoading, handleVaultClick };
};
